/* @flow */

import log from 'loglevel';
import 'core-js';
import * as React from 'react';
import { createRoot } from 'react-dom/client';

import type { Translation } from './helpers/localization';

import App from './App';
import { firebase, initializeFirebase } from './firebase';
import { logError, logInfo } from './helpers/debug/debug';
import { isRunningOnLocalhost } from './helpers/jsHelpers/environment';
import { parseBoolean, parseStringArray } from './helpers/jsHelpers/parser';
import Localizer from './helpers/localization';
import './index.css';
import ReducedMotionManager from './helpers/ui/reducedMotionManager';
import translationDe from './locales/de/translation.json';
import translationEn from './locales/en/translation.json';
import './themes/measurements-core.css';
import './themes/toastify-core.css';
import translationFr from './locales/fr/translation.json';

// Redirect HTTP to HTTPS and remove '/index.html' if no query string is present
const {
  location: { hostname, pathname, protocol, search },
} = window;
if (!isRunningOnLocalhost() && (protocol !== 'https:' || pathname === '/index.html')) {
  window.location.replace(`https://${hostname}${search}`);
}

// Dynamic Google Analytics initialization (moved from index.html)
const gaId: ?string = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;

if (gaId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', gaId);
}

// Dynamic Google Tag Manager initialization (moved from index.html)
const gtmId: ?string = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID;

if (gtmId) {
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ event: 'gtm.js', 'gtm.start': new Date().getTime() });
    let f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    if (f && f.parentNode) {
      f.parentNode.insertBefore(j, f);
    }
  })(window, document, 'script', 'dataLayer', gtmId);
}

// Firebase service worker registration
if (firebase.enabled && navigator.serviceWorker) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        logInfo(`Service Worker registered with scope: ${registration.scope}`);
      })
      .catch((error) => {
        logError(`Service Worker registration failed: ${error.message}`);
      });
  });
}

// Initialize Firebase (if configured for current app)
initializeFirebase();

const translations: Translation = {
  de: translationDe,
  en: translationEn,
  fr: translationFr,
};

// Languages supported by application
export const APP_SUPPORTED_LANGUAGES: Array<string> = parseStringArray(process.env.REACT_APP_LANGUAGES, ',', ['fr', 'en']);

// Get the best from supported languages and browser's languages
export const APP_LANGUAGES: Array<string> = Localizer.getAppLanguages(APP_SUPPORTED_LANGUAGES);
export const APP_LANGUAGES_AS_STRING: string = JSON.stringify(APP_LANGUAGES);

// First app language wins jackpot and is promoted official language for this session
export const [APP_LANGUAGE] = APP_LANGUAGES;

// Check log level from URL
const { searchParams } = new URL(window.location.href);
const urlLogLevel = searchParams.get('logLevel') ?? searchParams.get('loglevel') ?? searchParams.get('log_level') ?? '';
// Log level could be passed as number or name
const pos = ['0', '1', '2', '3', '4', 'trace', 'debug', 'info', 'warn', 'error'].indexOf(urlLogLevel.toLowerCase());
// eslint-disable-next-line no-magic-numbers
const requiredLogLevel = pos > -1 ? pos % 5 : null;

// If no log level is passed through ULR, log level is set to TRACE in debug mode (and test), and to WARN otherwise (production/staging)
const logLevel: number = requiredLogLevel ?? (parseBoolean(process.env.REACT_APP_DEBUG_MODE) || process.env.REACT_APP_ENV_NAME === 'test' ? log.levels.TRACE : log.levels.WARN);
const LOG_LEVELS = [log.levels.TRACE, log.levels.DEBUG, log.levels.INFO, log.levels.WARN, log.levels.ERROR];
log.setDefaultLevel(LOG_LEVELS[logLevel]);
log.setLevel(LOG_LEVELS[logLevel]);

// Initialize global motion preference listeners on app startup
ReducedMotionManager.initialize();

const rootElement: HTMLElement | null = document.getElementById('root');
if (rootElement) {
  // Initialize the localization module
  Localizer.initialize(APP_LANGUAGE, { [APP_LANGUAGE]: translations[APP_LANGUAGE] }).then(() => {
    createRoot(rootElement).render(<App />);
  });
} else {
  logError('No "root" element to render application');
}
